"use client";

import { cn } from "../../../lib/cn";
import {
	RippleButton,
	type SafeRippleButtonProps,
} from "../../material-ui/ripple-button";
import { forwardRef } from "react";
import { CtaContinuationCountdownStrip } from "./cta-continuation-countdown-strip";
import {
	CTA_RIPPLE_BUTTON_WHILE_HOVER,
	CTA_RIPPLE_BUTTON_WHILE_TAP,
} from "./cta-ripple-motion";
import {
	CTA_PRIMARY_ELEVATION_CLASS,
	CTA_PRIMARY_ELEVATION_COMPACT_CLASS,
} from "./cta-surface-classes";
import {
	PRIMARY_CTA_CLASSNAME,
	PRIMARY_CTA_GRADIENT_CHROME_CLASS,
} from "./primary-cta-classname";

export type CtaSize = "micro" | "small" | "medium" | "large";
export type CtaWidth = "auto" | "full" | "grow";
export type CtaMaxLines = 1 | 2;

export interface PrimaryCtaProps extends SafeRippleButtonProps {
	ctaSize?: CtaSize;
	ctaWidth?: CtaWidth;
	/**
	 * Text wrapping behavior for very narrow containers.
	 * - `1` (default): single line with truncation.
	 * - `2`: clamp to 2 lines, then truncate.
	 */
	maxLines?: CtaMaxLines;
	/**
	 * Topic continuation dwell (0–100 elapsed). When set and `asChild` is not used, renders a
	 * bottom countdown strip inside the button.
	 */
	continuationTimerElapsedPercent?: number;
	continuationTimerShowStrip?: boolean;
}

/**
 * {@link RippleButton}'s `asChild` branch renders `Button` + Slot only — it skips the
 * motion gradient chrome. Merge the same amber surface so `PrimaryCta asChild` (e.g. `Link`)
 * matches non-slotted primary CTAs.
 */
const PRIMARY_CTA_SLOTTED_CHROME = "relative overflow-hidden";

const PRIMARY_CTA_SIZE_CLASSNAME: Record<CtaSize, string> = {
	micro: "min-w-0 h-8 rounded-lg px-3 py-0 text-xs font-black uppercase tracking-wide leading-none",
	small: "min-w-0 h-9 rounded-lg px-5 py-0 text-xs font-black uppercase tracking-wide leading-none",
	medium: "min-w-[9rem] h-auto rounded-xl px-4 py-3 text-sm font-black uppercase tracking-wide leading-none",
	large: PRIMARY_CTA_CLASSNAME,
};

const PRIMARY_CTA_SIZE_ELEVATION_CLASSNAME: Record<CtaSize, string> = {
	micro: CTA_PRIMARY_ELEVATION_COMPACT_CLASS,
	small: CTA_PRIMARY_ELEVATION_COMPACT_CLASS,
	medium: CTA_PRIMARY_ELEVATION_CLASS,
	large: "",
};

const CTA_WIDTH_CLASSNAME: Record<CtaWidth, string> = {
	auto: "",
	full: "w-full",
	/** Use inside a `flex` row to get “half width” pairs. */
	grow: "flex-1 min-w-0",
};

const CTA_TEXT_MAX_LINES_CLASSNAME: Record<CtaMaxLines, string> = {
	1: "min-w-0 truncate",
	2: "min-w-0 whitespace-normal break-words line-clamp-2 leading-none",
};

/**
 * Primary CTA (amber gradient) — same as `RippleButton` + {@link PRIMARY_CTA_CLASSNAME}
 * Prefer PrimaryCta / SecondaryCta for product CTAs (briefing, completion overlays).
 */
export const PrimaryCta = forwardRef<HTMLButtonElement, PrimaryCtaProps>(
	function PrimaryCta(
		{
			className,
			children,
			type = "button",
			ctaSize = "medium",
			ctaWidth = "auto",
			maxLines = 1,
			onClick,
			continuationTimerElapsedPercent,
			continuationTimerShowStrip,
			appearance = "plain",
			whileHover = CTA_RIPPLE_BUTTON_WHILE_HOVER,
			whileTap = CTA_RIPPLE_BUTTON_WHILE_TAP,
			asChild,
			...props
		},
		ref,
	) {
		const label =
			maxLines === 2 &&
			(typeof children === "string" || typeof children === "number") ? (
				<span className={CTA_TEXT_MAX_LINES_CLASSNAME[2]}>
					{children}
				</span>
			) : maxLines === 1 &&
				(typeof children === "string" ||
					typeof children === "number") ? (
				<span className={CTA_TEXT_MAX_LINES_CLASSNAME[1]}>
					{children}
				</span>
			) : (
				children
			);

		const showContinuationStrip =
			continuationTimerElapsedPercent != null && !asChild;

		return (
			<RippleButton
				ref={ref}
				{...props}
				asChild={asChild}
				onClick={onClick}
				type={type}
				appearance={appearance}
				whileHover={whileHover}
				whileTap={whileTap}
				variant="ghost"
				className={cn(
					PRIMARY_CTA_GRADIENT_CHROME_CLASS,
					PRIMARY_CTA_SIZE_CLASSNAME[ctaSize],
					PRIMARY_CTA_SIZE_ELEVATION_CLASSNAME[ctaSize],
					CTA_WIDTH_CLASSNAME[ctaWidth],
					Boolean(asChild) && PRIMARY_CTA_SLOTTED_CHROME,
					showContinuationStrip && "overflow-hidden",
					className,
				)}
			>
				{asChild ? (
					label
				) : showContinuationStrip ? (
					<>
						<CtaContinuationCountdownStrip
							continuationTimerElapsedPercent={
								continuationTimerElapsedPercent
							}
							continuationTimerShowStrip={
								continuationTimerShowStrip
							}
						/>
						{label}
					</>
				) : (
					label
				)}
			</RippleButton>
		);
	},
);
